import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, connect } from 'react-redux';
import { Oval as Loader } from 'react-loader-spinner';
import PropTypes from 'prop-types';
import './PhotoUploader.css';
import CameraCapture from '../CameraCapture';
import { validateFileSize, isValidImage } from '../../shared/utils/fileValidation';
import { getContentType, processBannerImage, processImage, uploadFileToS3 } from '../../shared/utils/fileOperations';
import { UPLOAD_TYPE, ROLE } from '../../shared/constants/common';
import { showError, showSuccess } from '../../shared/utils/swalHandler';
import * as fileActions from '../../shared/actions/fileActions';
import { updateProfileImage, updateProfileBanner } from '../../shared/actions/userActions';
import { fetchUser } from '../../shared/actions/userActions';
import { fetchCreator } from '../../shared/actions/creatorActions';

/**
 * PhotoUploader Component
 * 
 * A reusable component for uploading photos via camera capture or file selection.
 * Suitable for profile pictures, banners, and any image upload scenarios.
 * 
 * @component
 * @param {Object} props
 * @param {string} props.uploadType - Type identifier for the upload ('picture', 'banner', or custom)
 * @param {string} props.buttonLabel - Label for the trigger button
 * @param {string} props.buttonIcon - Font Awesome icon class for the trigger button
 * @param {string} props.className - Additional CSS classes for the container
 * @param {string} props.menuPosition - Position of the upload menu ('top-left', 'top-right', 'bottom-left', 'bottom-right')
 * @param {boolean} props.showButton - Whether to show the trigger button
 * @param {React.Node} props.children - Custom trigger element (overrides default button)
 * @param {string} props.acceptedFormats - Accepted file formats (default: 'image/*')
 * @param {string} props.uploadingText - Text to display during upload
 */
const PhotoUploader = ({
    uploadType = 'picture',
    buttonLabel = 'Edit',
    buttonIcon = 'fa-pen-to-square',
    className = '',
    menuPosition = 'bottom-left',
    showButton = true,
    children = null,
    acceptedFormats = 'image/*',
    uploadingText = 'Uploading...',
    user,
    fetchUser,
    fetchCreator,
}) => {
    const dispatch = useDispatch();

    // State management
    const [menuOpen, setMenuOpen] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const fileInputRef = useRef(null);
    const containerRef = useRef(null);

    const handleMenuToggle = useCallback((event) => {
        if (event) {
            event.stopPropagation();
        }

        // Close any other open PhotoUploader menus first
        const closeEvent = new CustomEvent('closePhotoUploaderMenus', {
            detail: { excludeRef: containerRef.current }
        });
        document.dispatchEvent(closeEvent);

        setMenuOpen(prev => !prev);
    }, []);

    const handleTakePhoto = useCallback(() => {
        setMenuOpen(false);
        setShowCamera(true);
    }, []);

    const handleUploadPhoto = useCallback(() => {
        setMenuOpen(false);
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    const uploadImage = useCallback(async (file) => {
        const isAvatar = uploadType === UPLOAD_TYPE.PICTURE;
        const successMessage = isAvatar
            ? 'Profile picture updated successfully'
            : 'Banner updated successfully';
        const errorMessage = isAvatar
            ? 'Profile picture upload failed. Please try again.'
            : 'Banner upload failed. Please try again.';

        try {
            setProgress(0);
            // Validate file
            const isValid = await validateFileSize(file);
            if (!isValid) {
                setProgress(0);
                return;
            }
            setProgress(10);

            const contentType = getContentType(file);
            const validImage = await isValidImage(contentType);
            if (!validImage) {
                setProgress(0);
                return;
            }
            setProgress(20);
            setIsUploading(true);

            // Process image based on upload type
            const processedFile = uploadType === UPLOAD_TYPE.BANNER
                ? await processBannerImage(file)
                : await processImage(file);

            // Get presigned URL
            const presignedData = await fileActions.getPresignedUrl(processedFile, true);
            if (!presignedData.success || !presignedData?.data?.url) {
                throw new Error(presignedData.error || 'Failed to get presigned URL');
            }
            setProgress(40);

            // Upload to S3
            const signedUrl = presignedData.data.url;
            const uploadedUrl = await uploadFileToS3(signedUrl, processedFile, contentType, undefined, true);
            if (!uploadedUrl) {
                throw new Error('Failed to upload image');
            }
            setProgress(70);

            // Update profile
            const updateAction = uploadType === UPLOAD_TYPE.BANNER
                ? updateProfileBanner
                : updateProfileImage;

            const response = await updateAction(uploadedUrl);
            setProgress(100);

            if (response.success) {
                // update user and creator data on redux store
                fetchUser();
                if (user?.rol && (user.rol === ROLE.CREATOR_SUB || user.rol === ROLE.CREATOR_CONTENT)) {
                    fetchCreator(user.username);
                }
                // Keep loading state for a moment to show 100% completion
                setTimeout(() => {
                    setIsUploading(false);
                    setProgress(0);
                }, 500);
                await showSuccess("Success!", successMessage);
            } else {
                throw new Error(response.error || 'Failed to update profile');
            }
        } catch (error) {
            setIsUploading(false);
            setProgress(0);
            await showError("Error!", error.message || errorMessage);
        }
    }, [uploadType, dispatch]);

    /**
     * Handles file selection from file input
     */
    const handleFileSelect = useCallback((event) => {
        const file = event.target.files[0];
        if (file) {
            uploadImage(file);
        }
        // Reset file input value to allow selecting the same file again
        event.target.value = '';
    }, [uploadImage]);

    /**
     * Closes the camera capture interface
     */
    const handleCameraClose = useCallback(() => {
        setShowCamera(false);
    }, []);

    /**
     * Handles photo capture from camera
     */
    const handleCameraCapture = useCallback((file) => {
        setShowCamera(false);
        uploadImage(file);
    }, [uploadImage]);

    /**
     * Handles clicks outside the component to close the menu
     */
    const handleClickOutside = useCallback((event) => {
        if (containerRef.current && !containerRef.current.contains(event.target) && menuOpen) {
            setMenuOpen(false);
        }
    }, [menuOpen]);

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [handleClickOutside]);

    /**
     * Listen for close events from other PhotoUploader instances
     */
    useEffect(() => {
        const handleCloseMenus = (event) => {
            // Only close this menu if the event is from a different PhotoUploader instance
            if (event.detail?.excludeRef !== containerRef.current && menuOpen) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('closePhotoUploaderMenus', handleCloseMenus);
        return () => document.removeEventListener('closePhotoUploaderMenus', handleCloseMenus);
    }, [menuOpen]);

    /**
     * Renders a loading spinner with message
     */
    const renderLoadingSpinner = () => (
        <div className="photo-uploader-camera-loading">
            <Loader type="Oval" color="#f534bf" height={60} width={60} />
            <h4 className="photo-uploader-camera-loading-title">{uploadingText}</h4>
            <p className="photo-uploader-camera-loading-text">
                Please wait while we process your image
            </p>
            <div className="photo-uploader-progress-container">
                <div className="photo-uploader-progress-bar">
                    <div
                        className="photo-uploader-progress-fill"
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <p className="photo-uploader-progress-text">
                    {progress}%
                </p>
            </div>
        </div>
    );

    /**
     * Renders a menu item for the upload menu
     */
    const renderMenuItem = (icon, text, onClick) => (
        <button
            className="photo-uploader-menu-item"
            onClick={onClick}
            disabled={isUploading}
            type="button"
        >
            <div className="photo-uploader-menu-item-icon-container">
                <i className={`fa-solid ${icon} photo-uploader-menu-item-icon`} />
            </div>
            <span className="photo-uploader-menu-item-text">
                {isUploading ? uploadingText : text}
            </span>
        </button>
    );

    /**
     * Renders the camera overlay using a portal
     */
    const renderCameraOverlay = () => {
        if (!showCamera) return null;

        return ReactDOM.createPortal(
            <div className="photo-uploader-camera-overlay">
                <button
                    onClick={handleCameraClose}
                    className="photo-uploader-camera-close"
                    type="button"
                    aria-label="Close camera"
                >
                    ×
                </button>
                {isUploading ? renderLoadingSpinner() : (
                    <CameraCapture
                        onCapture={handleCameraCapture}
                        photoOnly={true}
                    />
                )}
            </div>,
            document.body
        );
    };

    /**
     * Renders the default trigger button
     */
    const renderTriggerButton = () => {
        if (!showButton || children) return null;

        return (
            <button
                className={`photo-uploader-trigger ${menuOpen ? 'photo-uploader-trigger-active' : ''}`}
                onClick={handleMenuToggle}
                disabled={isUploading}
                type="button"
            >
                <i className={`fa-solid ${buttonIcon} photo-uploader-trigger-icon`}></i>
                <span className="photo-uploader-trigger-text">{buttonLabel}</span>
            </button>
        );
    };

    return (
        <>
            <div
                ref={containerRef}
                className={`photo-uploader-container ${className}`}
                data-upload-type={uploadType}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptedFormats}
                    className="photo-uploader-file-input"
                    onChange={handleFileSelect}
                    aria-label="Upload photo from device"
                />

                {children ? (
                    <div onClick={handleMenuToggle} className="photo-uploader-custom-trigger">
                        {children}
                    </div>
                ) : (
                    renderTriggerButton()
                )}

                {menuOpen && (
                    <div className={`photo-uploader-menu photo-uploader-menu-${menuPosition}`}>
                        {renderMenuItem('fa-camera', 'Take Photo', handleTakePhoto)}
                        {renderMenuItem('fa-arrow-up-from-bracket', 'Upload Photo', handleUploadPhoto)}
                    </div>
                )}
            </div>

            {renderCameraOverlay()}

            {isUploading && !showCamera && ReactDOM.createPortal(
                <div className="photo-uploader-loading-overlay">
                    <div className="photo-uploader-loading-content">
                        {renderLoadingSpinner()}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

PhotoUploader.propTypes = {
    uploadType: PropTypes.string,
    buttonLabel: PropTypes.string,
    buttonIcon: PropTypes.string,
    className: PropTypes.string,
    menuPosition: PropTypes.oneOf(['top-left', 'top-right', 'bottom-left', 'bottom-right']),
    showButton: PropTypes.bool,
    children: PropTypes.node,
    acceptedFormats: PropTypes.string,
    uploadingText: PropTypes.string,
};

const mapStateToProps = (state) => {
    return {
        user: state.user.user,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        fetchUser: () => dispatch(fetchUser()),
        fetchCreator: (username) => dispatch(fetchCreator(username)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PhotoUploader);
